import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetLanguages } from "~/api/languages/languages";
import { Language } from "~/api/model";
import {
  getGetOrganizationQueryKey,
  useGetOrganization,
  useUpdateOrganization,
} from "~/api/organizations/organizations";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { UpdateButton, UpdateButtonRef } from "~/components/UpdateButton";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { trackEvent } from "~/utils/analytics";

type FormData = {
  language: Language;
};

export const SettingsOrganization: FC = () => {
  useTrackPageVisit("app/settings_organization/visit");

  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: organization } = useGetOrganization(organizationId);
  const { data: languages } = useGetLanguages();
  const { mutate, isPending } = useUpdateOrganization();
  const updateButtonRef = useRef<UpdateButtonRef>(null);
  const queryClient = useQueryClient();

  const { handleSubmit, setValue, watch } = useForm<FormData>();

  const currentLanguage = watch("language");

  const onSubmit = (data: FormData) => {
    trackEvent({
      eventName: "app/settings_organization/language.select/change",
      properties: { language: data.language },
    });

    mutate(
      { organizationId, data: { language: data.language } },
      {
        onSuccess: () => {
          updateButtonRef.current?.onSuccess();
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        },
      }
    );
  };

  useEffect(() => {
    if (organization?.language) {
      setValue("language", organization.language);
    }
  }, [organization?.language, setValue]);

  const handleLanguageChange = (value: string) => {
    setValue("language", value as Language);
  };

  return (
    <div className="flex flex-col gap-6 mt-2 w-[766px]">
      <h3 className="text-2xl font-semibold">
        <FormattedMessage
          id="settings.organization"
          defaultMessage="Organization"
        />
      </h3>
      <div className="flex gap-4 justify-between">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-2"
        >
          <Label htmlFor="organizationLanguage">
            <FormattedMessage
              id="settings.organization.outputLanguage"
              defaultMessage="Output Language"
            />
          </Label>
          <Select value={currentLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger
              id="organizationLanguage"
              onClick={() => {
                trackEvent({
                  eventName: "app/settings_organization/language.select/click",
                });
              }}
            >
              <SelectValue
                placeholder={intl.formatMessage({
                  id: "settings.organization.selectLanguage",
                  defaultMessage: "Select a language",
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {languages &&
                Object.entries(languages).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <span className="mb-4 text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.organization.outputLanguageHint"
              defaultMessage="This language will be used for AI-generated content such as summaries, classifications, and chat responses."
            />
          </span>

          <UpdateButton isPending={isPending} ref={updateButtonRef} />
        </form>
      </div>
    </div>
  );
};
